import { useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';

export interface UseCountUpOptions {
  /** Animation duration in ms. Default 900. */
  duration?: number;
  /** Decimal places to round the displayed value to. Default 0. */
  decimals?: number;
  /** Disable the count-up (e.g. for non-numeric tiles). Default true. */
  enabled?: boolean;
}

/**
 * useCountUp — animates a number from its previous value up to `target` with an
 * ease-out curve. Honours prefers-reduced-motion (jumps straight to target) and
 * cleans up its rAF on unmount / re-target. Returns the current rounded value.
 */
export function useCountUp(target: number, options: UseCountUpOptions = {}): number {
  const { duration = 900, decimals = 0, enabled = true } = options;
  const reduced = usePrefersReducedMotion();
  const [value, setValue] = useState<number>(enabled && !reduced ? 0 : target);
  const valueRef = useRef<number>(value);
  valueRef.current = value;

  useEffect(() => {
    if (!enabled || reduced || duration <= 0) {
      setValue(target);
      return;
    }
    const from = valueRef.current;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      setValue(from + (target - from) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
      else setValue(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, enabled, reduced]);

  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}
